import { ReactNode, PureComponent, ReactElement, lazy } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { WebNode } from '@API/WebNode';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import { SettingsList } from '@Primitive/SettingsList';
import { WebThingPort, WebThing } from '@API/WebThing';
import { IconURIPath } from '../../WidgetsThingsStructs';
import * as WidgetsThingsStructs from "../../WidgetsThingsStructs";
import { IWidgetThingsClass } from '../../Utils/IWidgetThingsClass';
import { getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { eIconType, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDescription, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { DateFormat, Formatters } from '@Utils/Formats';
import { am4ChartsTheme, amChartsData, PortKeys, updateConfigValidator } from './amChartsStructs';
import { ToolbarIconInfo, ToolbarIconInfo_GetSettingList } from '@Primitive/ToolbarIconButton';

const AmCharts = lazy(() => import("./amChartsAsync"));
// =================================================================================================
class AmChartsSettings extends PureComponent<IWidgetSettingsProps<amChartsData>> {
    constructor(props: IWidgetSettingsProps<amChartsData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<amChartsData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Enable Click Events",
                        dataPath: "enableClickEvents"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Enable Zoom Events",
                        dataPath: "enableZoomEvents"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Animations",
                        dataPath: "enableAnimations"
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Theme",
                        dataPath: "theme",
                        inSeperateLine: true,
                        items: [
                            { value: am4ChartsTheme.material, label: "Material" },
                            { value: am4ChartsTheme.amchartsdark, label: "amChartsDark" },
                            { value: am4ChartsTheme.amcharts, label: "amCharts" },
                            { value: am4ChartsTheme.dark, label: "Dark" },
                            { value: am4ChartsTheme.dataviz, label: "DataViz" },
                            { value: am4ChartsTheme.frozen, label: "Frozen" },
                            { value: am4ChartsTheme.kelly, label: "Kelly" },
                            { value: am4ChartsTheme.microchart, label: "Microchart" },
                            { value: am4ChartsTheme.moonrisekingdom, label: "Moon Rise Kingdom" },
                            { value: am4ChartsTheme.patterns, label: "Patterns" },
                            { value: am4ChartsTheme.spiritedaway, label: "Spirited Away" }
                        ]
                    }
                ]
            },
            {
                name: "Toolbar",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Toolbar Custom Icons",
                        tooltip: "Extra custom icons in toolbar",
                        dataPath: "customToolbarIcons",
                        itemsInfo: ToolbarIconInfo_GetSettingList(),
                        createNewItem: () => {
                            return {
                                icon: "",
                                tooltip: "Icon tooltip",
                                iconType: eIconType.fontAwesome,
                                id: "Icon " + getRandomSmallKey(),
                                iconColor: "#2c2c2c",
                                nestedItems: []
                            } as ToolbarIconInfo;
                        }
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Height",
                        dataPath: "toolbarHeight",
                        minValue: 20,
                        maxValue: 80,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Icon Size",
                        dataPath: "toolbarIconSize",
                        minValue: 14,
                        maxValue: 24,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Default Icons Color',
                        tooltip: 'Set value for default Icons.',
                        dataPath: "defaultIconsColor",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Font Color',
                        tooltip: 'Set font color.',
                        dataPath: "toolbarFontColor",
                    },
                ]
            },
            {
                name: "Date Range",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Date Range',
                        tooltip: 'If enabled, a date range picker will be shown in the toolbar.',
                        dataPath: "showDateRange"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Show Realtime button",
                        tooltip: 'Append RealTime switch inside widget',
                        dataPath: "showRealTimeFlag",
                        isHidden: (data: amChartsData) => !data.showDateRange
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Initial Realtime state",
                        tooltip: '',
                        dataPath: "initialRealtimeData",
                        isHidden: (data: amChartsData) => !data.showDateRange || !data.showRealTimeFlag
                    },
                    {
                        type: SettingsType.Number,
                        label: "Initial Days Period",
                        dataPath: "initialDateRangeDays",
                        tooltip: 'Specify the initial period of the range in days. Default value is set to 30 days, meaning that when you load the page, the range will span from current day to 30 days before.',
                        minValue: 0,
                        isHidden: (data: amChartsData) => !data.showDateRange,
                        oneLine: true
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Selection of dates with time",
                        tooltip: 'Whether to include time in range selection or not.',
                        dataPath: "timePicker",
                        isHidden: (data: amChartsData) => !data.showDateRange
                    },
                    {
                        type: SettingsType.Number,
                        label: "Time Picker increment (min)",
                        dataPath: "timePickerIncrement",
                        tooltip: 'In case you enable time selection, you can specify the step of the time picker in minutes.',
                        maxValue: 60,
                        minValue: 0,
                        isHidden: (data: amChartsData) => !data.showDateRange || !data.timePicker,
                        oneLine: true
                    },
                    {
                        type: SettingsType.Number,
                        label: "Initial Hour Period",
                        tooltip: "Specify the initial period of the range in hours.",
                        dataPath: "timePickerInitialRange",
                        minValue: 0,
                        isHidden: (data: amChartsData) => !data.showDateRange || !data.timePicker,
                        oneLine: true
                    }
                ]
            },
            {
                name: "Excel Download",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Excel Download',
                        tooltip: 'Allows to download an excel spreadsheet (XLSX file) with the extracted data from the chart',
                        dataPath: 'excelDownload'
                    },
                    {
                        type: SettingsType.String,
                        label: "File Name",
                        dataPath: "excelOptions.fileName",
                        tooltip: 'Define excel file name',
                        oneLine: true,
                        isHidden: (data: amChartsData) => !data.excelDownload
                    },
                ]
            },
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
        />;
    }
}
// =================================================================================================
export class amChartsWidget extends IWidgetThingsClass<amChartsData> {
    Render(key: string, width: number, height: number): ReactNode {
        return <WidgetsThingsStructs.LazyWidget>
            <AmCharts />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <AmChartsSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];
        let wData = this.WidgetData;

        // Calculate the range
        let startDate = moment().startOf('minute').subtract(wData.initialDateRangeDays, 'days');
        if (wData.timePicker)
            startDate.subtract(wData.timePickerInitialRange, "hours");
        let endDate = moment().startOf('minute');
        let dates = Formatters.datetimeRange(startDate, endDate, DateFormat.ISO, wData.timePicker, true).split(' - ');

        ports.push(new WebThingPort({
            Name: 'Data',
            PortKey: PortKeys.Data,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            State: ''
        }, WebThingPortType.Data, data => this.ValidateValues(PortKeys.Data, data)));

        ports.push(new WebThingPort({
            Name: 'Update Config',
            PortKey: PortKeys.UpdateConfig,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            State: ''
        }, WebThingPortType.Data, data => this.ValidateValues(PortKeys.UpdateConfig, data)));

        ports.push(new WebThingPort({
            Name: 'Date Range',
            PortKey: PortKeys.DateRange,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            State: ''
        }, WebThingPortType.Data, data => this.ValidateValues(PortKeys.DateRange, data)));

        ports.push(new WebThingPort({
            Name: "Selected Data",
            PortKey: PortKeys.SelectedData,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ""
        }, WebThingPortType.Trigger));

        ports.push(new WebThingPort({
            Name: "Selected Range",
            PortKey: PortKeys.SelectedRange,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            State: JSON.stringify({ StartDate: dates[0], EndDate: dates[1] })
        }, WebThingPortType.Data));

        ports.push(new WebThingPort({
            Name: 'Selected Zoom Range',
            PortKey: PortKeys.SelectedZoomRange,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ''
        }, WebThingPortType.Data));

        ports.push(new WebThingPort({
            Name: 'Cursor Selection',
            PortKey: PortKeys.CursorSelection,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ''
        }, WebThingPortType.Data));

        ports.push(new WebThingPort({
            Name: "Real Time",
            PortKey: PortKeys.RealTime,
            Type: Plegma.ePortType.Boolean,
            ioDirection: Plegma.ioPortDirection.Output,
            State: !wData.initialRealtimeData || !wData.showRealTimeFlag ? "False" : "True"
        }, WebThingPortType.Data));

        ports.push(new WebThingPort({
            Name: 'onToolbarIconClick',
            PortKey: PortKeys.onToolbarIconClick,
            Type: Plegma.ePortType.String,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ""
        }, WebThingPortType.Data));

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): amChartsData {
        return {
            theme: am4ChartsTheme.amcharts,
            enableAnimations: false,
            enableClickEvents: false,
            enableZoomEvents: false,
            showDateRange: false,
            initialDateRangeDays: 30,
            timePicker: false,
            timePickerIncrement: 5,
            timePickerInitialRange: 0,
            initialRealtimeData: true,
            showRealTimeFlag: false,
            excelDownload: false,
            excelOptions: {
                fileName: "Data"
            },
            customToolbarIcons: [],
            toolbarHeight: 25,
            toolbarIconSize: 15
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return amChartsWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'amChart',
            WidgetTitle: '',
            Key: 'amChartsWidget',
            Type: 'amChartsWidget',
            Data: amChartsWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // validation for incoming event
        switch (key) {
            case PortKeys.UpdateConfig:
                return updateConfigValidator().DataValidateString(value);
            default:
                return { valid: true, errors: '' };
        }
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: amChartsData): amChartsData {
        return data;
    }
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Data; };
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}